import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, Calculator, Grid3X3 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalEncryptions: number;
  totalDecryptions: number;
  totalOperations: number;
  totalCharactersProcessed: number;
  averageTextLength: number;
  longestText: number;
  blocksProcessed: number;
  uniqueKeys: number;
  matrixOperations: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  keyMatrix: number[][];
  blockSize: number;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
  textLength: number;
  blocksCount: number;
}

const HillCipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [keyMatrix, setKeyMatrix] = useState<number[][]>([[3, 2], [5, 7]]);
  const [blockSize, setBlockSize] = useState<number>(2);
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | 'matrix' | null>(null);
  
  // Advanced options state
  const [removeSpaces, setRemoveSpaces] = useState<boolean>(true);
  const [fillerLetter, setFillerLetter] = useState<string>('X');
  const [groupOutput, setGroupOutput] = useState<boolean>(true);
  const [showMatrix, setShowMatrix] = useState<boolean>(false);
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalEncryptions: 0,
    totalDecryptions: 0,
    totalOperations: 0,
    totalCharactersProcessed: 0,
    averageTextLength: 0,
    longestText: 0,
    blocksProcessed: 0,
    uniqueKeys: 0,
    matrixOperations: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);
  const [usedKeys, setUsedKeys] = useState<Set<string>>(new Set());

  // Mathematical helper functions
  const gcd = (a: number, b: number): number => b === 0 ? Math.abs(a) : gcd(b, a % b);

  const modInverse = (a: number, m: number): number => {
    if (gcd(a, m) !== 1) return -1;
    
    let m0 = m;
    let x0 = 0;
    let x1 = 1;
    
    if (m === 1) return 0;
    
    while (a > 1) {
      const q = Math.floor(a / m);
      let t = m;
      m = a % m;
      a = t;
      t = x0;
      x0 = x1 - q * x0;
      x1 = t;
    }
    
    if (x1 < 0) x1 += m0;
    return x1;
  };

  const determinant2x2 = (matrix: number[][]): number => {
    return matrix[0][0] * matrix[1][1] - matrix[0][1] * matrix[1][0];
  };

  const determinant3x3 = (matrix: number[][]): number => {
    return (
      matrix[0][0] * (matrix[1][1] * matrix[2][2] - matrix[1][2] * matrix[2][1]) -
      matrix[0][1] * (matrix[1][0] * matrix[2][2] - matrix[1][2] * matrix[2][0]) +
      matrix[0][2] * (matrix[1][0] * matrix[2][1] - matrix[1][1] * matrix[2][0])
    );
  };

  const calculateDeterminant = (matrix: number[][]): number => {
    if (matrix.length === 2) return determinant2x2(matrix);
    if (matrix.length === 3) return determinant3x3(matrix);
    return 0; // For larger matrices, simplified
  };

  const matrixInverse2x2 = (matrix: number[][]): number[][] | null => {
    const det = determinant2x2(matrix);
    const detMod = ((det % 26) + 26) % 26;
    const detInv = modInverse(detMod, 26);
    
    if (detInv === -1) return null;
    
    return [
      [((matrix[1][1] * detInv) % 26 + 26) % 26, ((-matrix[0][1] * detInv) % 26 + 26) % 26],
      [((-matrix[1][0] * detInv) % 26 + 26) % 26, ((matrix[0][0] * detInv) % 26 + 26) % 26]
    ];
  };

  const matrixInverse3x3 = (matrix: number[][]): number[][] | null => {
    const det = determinant3x3(matrix);
    const detMod = ((det % 26) + 26) % 26;
    const detInv = modInverse(detMod, 26);
    
    if (detInv === -1) return null;
    
    const adj = [
      [
        ((matrix[1][1] * matrix[2][2] - matrix[1][2] * matrix[2][1]) * detInv % 26 + 26) % 26,
        ((matrix[0][2] * matrix[2][1] - matrix[0][1] * matrix[2][2]) * detInv % 26 + 26) % 26,
        ((matrix[0][1] * matrix[1][2] - matrix[0][2] * matrix[1][1]) * detInv % 26 + 26) % 26
      ],
      [
        ((matrix[1][2] * matrix[2][0] - matrix[1][0] * matrix[2][2]) * detInv % 26 + 26) % 26,
        ((matrix[0][0] * matrix[2][2] - matrix[0][2] * matrix[2][0]) * detInv % 26 + 26) % 26,
        ((matrix[0][2] * matrix[1][0] - matrix[0][0] * matrix[1][2]) * detInv % 26 + 26) % 26
      ],
      [
        ((matrix[1][0] * matrix[2][1] - matrix[1][1] * matrix[2][0]) * detInv % 26 + 26) % 26,
        ((matrix[0][1] * matrix[2][0] - matrix[0][0] * matrix[2][1]) * detInv % 26 + 26) % 26,
        ((matrix[0][0] * matrix[1][1] - matrix[0][1] * matrix[1][0]) * detInv % 26 + 26) % 26
      ]
    ];
    
    return adj;
  };

  const getMatrixInverse = (matrix: number[][]): number[][] | null => {
    if (matrix.length === 2) return matrixInverse2x2(matrix);
    if (matrix.length === 3) return matrixInverse3x3(matrix);
    return null;
  };

  const multiplyMatrixVector = (matrix: number[][], vector: number[]): number[] => {
    const result = [];
    for (let i = 0; i < matrix.length; i++) {
      let sum = 0;
      for (let j = 0; j < vector.length; j++) {
        sum += matrix[i][j] * vector[j];
      }
      result.push(sum % 26);
    }
    return result;
  };

  const prepareText = (text: string): string => {
    let prepared = text.replace(/[^A-Z]/g, '').toUpperCase();
    
    while (prepared.length % blockSize !== 0) {
      prepared += fillerLetter;
    }
    
    return prepared;
  };

  const hillCipher = useCallback((text: string, matrix: number[][], isEncrypt: boolean): string => {
    if (!text || !matrix || matrix.length === 0) return '';
    
    const preparedText = prepareText(text);
    let result = '';
    
    const workingMatrix = isEncrypt ? matrix : getMatrixInverse(matrix);
    if (!workingMatrix) return 'ERROR: Matrix not invertible';
    
    for (let i = 0; i < preparedText.length; i += blockSize) {
      const block = preparedText.slice(i, i + blockSize);
      const vector = block.split('').map(char => char.charCodeAt(0) - 65);
      
      const resultVector = multiplyMatrixVector(workingMatrix, vector);
      const resultBlock = resultVector.map(num => String.fromCharCode((num % 26 + 26) % 26 + 65)).join('');
      
      result += resultBlock;
    }
    
    if (groupOutput && result && !result.startsWith('ERROR')) {
      const groups = [];
      for (let i = 0; i < result.length; i += blockSize) {
        groups.push(result.slice(i, i + blockSize));
      }
      result = groups.join(' ');
    }
    
    return result;
  }, [blockSize, fillerLetter, groupOutput]);

  const processText = useCallback(() => {
    if (!inputText || keyMatrix.length === 0) {
      setOutputText('');
      return '';
    }
    
    const result = hillCipher(inputText, keyMatrix, operation === 'encrypt');
    setOutputText(result);
    return result;
  }, [inputText, keyMatrix, operation, hillCipher]);

  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || !result || result.startsWith('ERROR')) return;
    
    const cleanInput = inputText.replace(/[^A-Z]/g, '').toUpperCase();
    const blocksCount = Math.ceil(cleanInput.length / blockSize);
    const keyString = JSON.stringify(keyMatrix);
    
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      keyMatrix: JSON.parse(JSON.stringify(keyMatrix)),
      blockSize: blockSize,
      operation,
      timestamp: new Date(),
      textLength: inputText.length,
      blocksCount: blocksCount
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]);
    
    setUsedKeys(prev => {
      const newSet = new Set(prev).add(keyString);
      
      setStats(prevStats => ({
        ...prevStats,
        totalEncryptions: prevStats.totalEncryptions + (operation === 'encrypt' ? 1 : 0),
        totalDecryptions: prevStats.totalDecryptions + (operation === 'decrypt' ? 1 : 0),
        totalOperations: prevStats.totalOperations + 1,
        totalCharactersProcessed: prevStats.totalCharactersProcessed + inputText.length,
        averageTextLength: Math.round((prevStats.totalCharactersProcessed + inputText.length) / (prevStats.totalOperations + 1)),
        longestText: Math.max(prevStats.longestText, inputText.length),
        blocksProcessed: prevStats.blocksProcessed + blocksCount,
        uniqueKeys: newSet.size,
        matrixOperations: prevStats.matrixOperations + blocksCount
      }));
      
      return newSet;
    });
  }, [inputText, keyMatrix, blockSize, operation]);

  useEffect(() => {
    if (inputText && keyMatrix.length > 0) {
      const result = processText();
      if (result && !result.startsWith('ERROR')) {
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
    }
  }, [inputText, keyMatrix, operation, blockSize]);

  useEffect(() => {
    if (keyMatrix.length !== blockSize) {
      if (blockSize === 2) {
        setKeyMatrix([[3, 2], [5, 7]]);
      } else if (blockSize === 3) {
        setKeyMatrix([[6, 24, 1], [13, 16, 10], [20, 17, 15]]);
      }
    }
  }, [blockSize]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setKeyMatrix([[3, 2], [5, 7]]);
    setBlockSize(2);
    setOperation('encrypt');
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText.replace(/\s/g, ''));
    setOutputText(temp);
    setOperation(operation === 'encrypt' ? 'decrypt' : 'encrypt');
  };

  const generateSampleText = () => {
    const samples = [
      "MATHEMATICS IS THE LANGUAGE OF SCIENCE",
      "LINEAR ALGEBRA TRANSFORMS CRYPTOGRAPHY",
      "MATRIX MULTIPLICATION SECURES MESSAGES",
      "HILL CIPHER USES MATHEMATICAL MATRICES",
      "POLYGRAPHIC SUBSTITUTION ENCRYPTS BLOCKS"
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  const generateNewMatrix = () => {
    let newMatrix: number[][];
    let attempts = 0;
    
    do {
      if (blockSize === 2) {
        newMatrix = [
          [Math.floor(Math.random() * 26), Math.floor(Math.random() * 26)],
          [Math.floor(Math.random() * 26), Math.floor(Math.random() * 26)]
        ];
      } else {
        newMatrix = [
          [Math.floor(Math.random() * 26), Math.floor(Math.random() * 26), Math.floor(Math.random() * 26)],
          [Math.floor(Math.random() * 26), Math.floor(Math.random() * 26), Math.floor(Math.random() * 26)],
          [Math.floor(Math.random() * 26), Math.floor(Math.random() * 26), Math.floor(Math.random() * 26)]
        ];
      }
      attempts++;
    } while (getMatrixInverse(newMatrix) === null && attempts < 100);
    
    if (getMatrixInverse(newMatrix) !== null) {
      setKeyMatrix(newMatrix);
    }
  };

  const updateMatrixElement = (row: number, col: number, value: string) => {
    const numValue = parseInt(value) || 0;
    const newMatrix = [...keyMatrix];
    newMatrix[row][col] = ((numValue % 26) + 26) % 26;
    setKeyMatrix(newMatrix);
  };

  return (
    <>
      <Helmet>
        <title>Hill Cipher - Mathematical Matrix Encryption Tool | Linear Algebra Cryptography</title>
        <meta name="description" content="Professional Hill Cipher tool using matrix multiplication and linear algebra for polygraphic substitution encryption. Features 2×2, 3×3 matrix sizes with modular arithmetic operations for advanced cryptography education." />
        <meta name="keywords" content="hill cipher, matrix encryption, linear algebra cryptography, polygraphic cipher, mathematical encryption, modular arithmetic, matrix multiplication cipher, determinant cryptography" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Hill Cipher - Mathematical Matrix Encryption Tool" />
        <meta property="og:description" content="Advanced Hill Cipher with matrix operations, linear algebra, and modular arithmetic. Perfect for mathematics and cryptography education." />
        
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Calculator className="text-indigo-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">Hill Cipher</h1>
                <p className="text-sm opacity-60">Mathematical Matrix Encryption</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setActiveModal(activeModal === 'matrix' ? null : 'matrix')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="View Key Matrix"
              >
                <Grid3X3 size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Info size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="encrypt">🔒 Encrypt</option>
                    <option value="decrypt">🔓 Decrypt</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Block Size</label>
                  <select
                    value={blockSize}
                    onChange={(e) => setBlockSize(Number(e.target.value))}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value={2}>2×2 Matrix</option>
                    <option value={3}>3×3 Matrix</option>
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateNewMatrix}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-indigo-600 border-indigo-500 hover:bg-indigo-700 text-white' : 'bg-indigo-500 border-indigo-400 hover:bg-indigo-600 text-white'
                    } transition-colors`}
                    title="Generate new invertible matrix"
                  >
                    <Calculator size={16} />
                    <span>New Key</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateSampleText}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                    title="Generate sample text"
                  >
                    <Grid3X3 size={16} />
                    <span>Sample</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
              
              {showMatrix && keyMatrix.length > 0 && (
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <h4 className="text-sm font-medium mb-3">Current Key Matrix ({blockSize}×{blockSize}):</h4>
                  <div className="grid gap-1 w-fit mx-auto" style={{gridTemplateColumns: `repeat(${blockSize}, minmax(0, 1fr))`}}>
                    {keyMatrix.flat().map((value, index) => (
                      <div
                        key={index}
                        className={`w-12 h-12 flex items-center justify-center text-sm font-mono font-bold border ${
                          isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        {value}
                      </div>
                    ))}
                  </div>
                  <div className="text-xs opacity-60 text-center mt-2">
                    Det = {calculateDeterminant(keyMatrix)} (mod 26)
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Input Text</h3>
                  <div className="text-sm opacity-60">
                    {inputText.length} characters
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter text to encrypt or decrypt using Hill cipher..."
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>

              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Output Text</h3>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm opacity-60">
                      {outputText.length} characters
                    </div>
                    <button
                      onClick={() => copyToClipboard(outputText)}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Copy to clipboard"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => downloadText(outputText, 'hill-cipher-output.txt')}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Download as file"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={outputText}
                  readOnly
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Matrix Modal */}
        {activeModal === 'matrix' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Grid3X3 className="text-indigo-500" />
                  <span>Key Matrix ({blockSize}×{blockSize})</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid gap-2 mx-auto w-fit" style={{gridTemplateColumns: `repeat(${blockSize}, minmax(0, 1fr))`}}>
                  {keyMatrix.map((row, i) =>
                    row.map((value, j) => (
                      <input
                        key={`${i}-${j}`}
                        type="number"
                        value={value}
                        onChange={(e) => updateMatrixElement(i, j, e.target.value)}
                        min="0"
                        max="25"
                        className={`w-16 h-16 text-center text-lg font-mono border rounded ${
                          isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    ))
                  )}
                </div>
                
                <div className="text-center space-y-2">
                  <div className="text-sm">
                    <strong>Determinant:</strong> {calculateDeterminant(keyMatrix)} (mod 26)
                  </div>
                  <div className="text-sm">
                    <strong>Invertible:</strong> {getMatrixInverse(keyMatrix) ? '✅ Yes' : '❌ No'}
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={generateNewMatrix}
                    className={`flex-1 p-2 text-sm rounded border ${
                      isDarkMode ? 'bg-indigo-600 border-indigo-500 hover:bg-indigo-700 text-white' : 'bg-indigo-500 border-indigo-400 hover:bg-indigo-600 text-white'
                    } transition-colors`}
                  >
                    Generate Random
                  </button>
                  <button
                    onClick={() => {
                      if (blockSize === 2) setKeyMatrix([[3, 2], [5, 7]]);
                      if (blockSize === 3) setKeyMatrix([[6, 24, 1], [13, 16, 10], [20, 17, 15]]);
                    }}
                    className={`flex-1 p-2 text-sm rounded border ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                  >
                    Default
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Calculator className="text-indigo-500" />
                  <span>About Hill Cipher</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is the Hill Cipher?</h3>
                  <p>The Hill cipher is a polygraphic substitution cipher based on linear algebra. Invented by Lester S. Hill in 1929, it encrypts blocks of letters using matrix multiplication with modular arithmetic, making it significantly more secure than simple substitution ciphers.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How It Works</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Choose an invertible n×n key matrix (determinant coprime to 26)</li>
                    <li>Convert plaintext to numbers (A=0, B=1, ..., Z=25)</li>
                    <li>Group plaintext into blocks of size n</li>
                    <li>Multiply each block vector by the key matrix (mod 26)</li>
                    <li>Convert result back to letters</li>
                    <li>For decryption, use the matrix inverse</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Mathematical Example (2×2)</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} font-mono text-xs space-y-2`}>
                    <div><strong>Key Matrix K:</strong></div>
                    <div>| 3  2 |</div>
                    <div>| 5  7 |</div>
                    <div><strong>Plaintext:</strong> "HI" → [7, 8]</div>
                    <div><strong>Encryption:</strong> K × [7, 8]ᵀ = [37, 91] ≡ [11, 13] (mod 26) → "LN"</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Key Requirements</h3>
                  <ul className="space-y-1">
                    <li><strong>Invertible Matrix:</strong> Determinant must be coprime to 26</li>
                    <li><strong>Coprime Numbers to 26:</strong> 1, 3, 5, 7, 9, 11, 15, 17, 19, 21, 23, 25</li>
                    <li><strong>Matrix Inverse:</strong> Must exist in modular arithmetic</li>
                    <li><strong>Block Size:</strong> Matrix dimensions determine block processing size</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Advantages</h3>
                  <ul className="space-y-1">
                    <li>Much stronger than monoalphabetic ciphers</li>
                    <li>Resistant to frequency analysis of individual letters</li>
                    <li>Scalable security with larger matrix sizes</li>
                    <li>Based on well-established mathematical principles</li>
                    <li>Suitable for mechanical/electronic implementation</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Level</h3>
                  <p className="text-orange-600">⚠️ <strong>Educational Security:</strong> The Hill cipher provides good resistance to simple attacks but can be cryptanalyzed with sufficient known plaintext. It's excellent for teaching mathematical cryptography but not suitable for modern security applications.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Remove Spaces</label>
                  <input
                    type="checkbox"
                    checked={removeSpaces}
                    onChange={(e) => setRemoveSpaces(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Group Output (Blocks)</label>
                  <input
                    type="checkbox"
                    checked={groupOutput}
                    onChange={(e) => setGroupOutput(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Show Matrix Preview</label>
                  <input
                    type="checkbox"
                    checked={showMatrix}
                    onChange={(e) => setShowMatrix(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Filler Letter</label>
                  <input
                    type="text"
                    value={fillerLetter}
                    onChange={(e) => setFillerLetter(e.target.value.replace(/[^A-Z]/g, '').slice(0, 1))}
                    placeholder="X"
                    maxLength={1}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                  <div className="text-xs opacity-60 mt-1">
                    Letter used to pad incomplete blocks
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Matrix Presets</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setBlockSize(2);
                        setKeyMatrix([[3, 2], [5, 7]]);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-indigo-600 border-indigo-500 hover:bg-indigo-700 text-white' : 'bg-indigo-500 border-indigo-400 hover:bg-indigo-600 text-white'
                      } transition-colors`}
                    >
                      Classic 2×2 Matrix
                    </button>
                    
                    <button
                      onClick={() => {
                        setBlockSize(3);
                        setKeyMatrix([[6, 24, 1], [13, 16, 10], [20, 17, 15]]);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-purple-600 border-purple-500 hover:bg-purple-700 text-white' : 'bg-purple-500 border-purple-400 hover:bg-purple-600 text-white'
                      } transition-colors`}
                    >
                      Secure 3×3 Matrix
                    </button>
                    
                    <button
                      onClick={() => {
                        setRemoveSpaces(true);
                        setGroupOutput(true);
                        setShowMatrix(true);
                        setFillerLetter('X');
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                      } transition-colors`}
                    >
                      Educational Mode
                    </button>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Matrix Information</h3>
                  <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} text-xs space-y-1`}>
                    <div><strong>Current Size:</strong> {blockSize}×{blockSize}</div>
                    <div><strong>Determinant:</strong> {calculateDeterminant(keyMatrix)}</div>
                    <div><strong>Invertible:</strong> {getMatrixInverse(keyMatrix) ? 'Yes' : 'No'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.totalEncryptions}</div>
                  <div className="text-sm">Total Encryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.totalDecryptions}</div>
                  <div className="text-sm">Total Decryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.blocksProcessed}</div>
                  <div className="text-sm">Blocks Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.matrixOperations}</div>
                  <div className="text-sm">Matrix Operations</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.uniqueKeys}</div>
                  <div className="text-sm">Unique Keys</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-cyan-500">{stats.averageTextLength}</div>
                  <div className="text-sm">Avg Text Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-indigo-500">{stats.totalOperations}</div>
                  <div className="text-sm">Total Operations</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-red-500">{stats.longestText}</div>
                  <div className="text-sm">Longest Text</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.operation === 'encrypt' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                          }`}>
                            {item.operation.toUpperCase()}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                            {item.blockSize}×{item.blockSize}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-indigo-500 text-white">
                            {item.blocksCount} blocks
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate font-mono">{item.input}</div>
                        <div className="truncate opacity-60 font-mono">→ {item.output}</div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <Calculator size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start encrypting or decrypting to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default HillCipherApp;